import React from "react";
import { useScannerContext } from "./ScannerContext";
import type { ScannerRegistrationConfig, ScannerScanTarget, ScannerStatus } from "./types";

/**
 * Регистрирует обработчик аппаратного сканирования для текущего экрана или overlay.
 * @param config параметры обработчика и UX-подсказки
 */
export function useScannerRegistration(config: ScannerRegistrationConfig): void {
    const { register, update } = useScannerContext();
    const registrationIdRef = React.useRef<string | null>(null);
    const configRef = React.useRef(config);
    configRef.current = config;
    const handleScan = React.useCallback((barcode: string) => configRef.current.onScan(barcode), []);

    React.useEffect(() => {
        const initialConfig = configRef.current;
        const handle = register({
            ...initialConfig,
            onScan: handleScan,
        });
        registrationIdRef.current = handle.id;

        return () => {
            handle.unregister();
            registrationIdRef.current = null;
        };
    }, [handleScan, register]);

    React.useEffect(() => {
        const id = registrationIdRef.current;

        if (id == null) {
            return;
        }

        update(id, { ...config, onScan: handleScan });
    }, [config, handleScan, update]);
}

/**
 * Возвращает состояние активного scanner-сценария для UI.
 * @returns статус активного обработчика и его подсказка
 */
export function useScannerStatus(): ScannerStatus {
    return useScannerContext().status;
}

/**
 * Захватывает текущий active handler как цель отложенного inject. \
 * Нужен перед blocking UI: после `globalSuppress` live active entry пропадает.
 *
 * @returns `captureActiveScanTarget` из scanner context
 * @example
 * ```tsx
 * const captureActiveScanTarget = useCaptureActiveScanTarget();
 * const target = captureActiveScanTarget();
 * if (target != null) {
 *   openManualBarcodeEntry(target);
 * }
 * ```
 */
export function useCaptureActiveScanTarget(): () => ScannerScanTarget | null {
    return useScannerContext().captureActiveScanTarget;
}
